import type { PackageRequirements, CompatibilityAssessment } from '../software/compatibility';
import { z } from 'zod';

/** Check-in freshness window shared by staff search and customer-portal connection labels. */
export const AGENT_OFFLINE_AFTER_MS = 10 * 60_000;

// Agent → server payloads. Every field is bounded; text from a device is
// untrusted and is stored and shown as data, never interpreted.
// PostgreSQL can't store a NUL character in text, and Windows registry values sometimes carry one (an installed
// program's entry, say): it is dropped rather than failing the whole report.
const text = (max: number) => z.string().overwrite(s => s.replaceAll('\u0000', '')).trim().max(max);
const iso = z.iso.datetime({ offset: true });
/**
 * One socket of WITTCO Remote's MeshAgent and the process holding it (WRMM-372; agent remote::Owner): the service or a
 * child MeshAgent started, the first flag of the child's command line if MeshAgent uses it for its children, its Windows
 * session, the kind of address, and when the socket was created against the process's start, as Windows reports both
 * (an observation only: Windows keeps crediting a socket to its creator's process ID, but this doesn't prove a reuse).
 */
export const remoteInboundOwner = z.object({
  protocol: z.enum(['tcp', 'udp']), port: z.number().int().min(0).max(65535), process: z.enum(['service', 'child']),
  session: z.enum(['service', 'user', 'unknown']), flag: z.enum(['none', '-kvm0', '-kvm1', '-exec', '-b64exec', '--slave', 'other']),
  address: z.enum(['any', 'private', 'link-local', 'public', 'other']),
  socketCreated: z.enum(['before-process', 'after-process', 'same-time', 'unavailable']),
}).strict();
/**
 * What the inventory endpoint accepts beyond what every agent sends, named in each acknowledgment (`accepts`): an agent
 * sends an optional field only once an acknowledgment named it, so an older API never receives it (WRMM-372).
 */
export const INVENTORY_ACCEPTS = ['remoteInbound.owners'] as const;
const bytes = z.number().int().min(0).max(2 ** 53 - 1);

/**
 * The people signed in at the computer (Linux: local sessions only; Windows: active console and Remote Desktop sessions):
 * seconds since their last keyboard or mouse input
 * (null when the device can't tell), and whether their screen is locked (null when unknown). Information for the
 * technician only; nothing is refused or delayed by it (Harry, 2026-10-21, WRMM-159).
 */
const presenceSessions = z.array(z.object({
  user: text(120).min(1), idleSeconds: z.number().int().min(0).max(10 * 365 * 86_400).nullable(), locked: z.boolean().nullable(),
}).strict()).max(16);
export type PresenceSession = z.infer<typeof presenceSessions>[number];

/** Read-only cached Windows Update results (WRMM-175). Optional so deployed older agents still ingest. */
const updateGuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);
export const windowsUpdatesSchema = z.object({
  source: z.literal('wua-offline'), status: z.enum(['complete', 'partial', 'unavailable']),
  totalCount: z.number().int().min(0).max(10000).nullable(),
  items: z.array(z.object({
    id: updateGuid, revision: z.number().int().min(0).max(2 ** 32 - 1), title: text(300).min(1),
    kbIds: z.array(z.string().regex(/^\d{1,12}$/)).max(16),
    classifications: z.array(z.object({ id: updateGuid, name: text(100).min(1) }).strict()).max(8),
    sizeBytes: bytes.nullable(), downloaded: z.boolean().nullable(),
    reboot: z.enum(['never', 'required', 'may-require', 'unknown']),
  }).strict()).max(100),
}).strict().superRefine((v, ctx) => {
  const error = (message: string) => ctx.addIssue({ code: 'custom', message });
  if (v.status === 'complete' && v.totalCount !== v.items.length) error('Complete search must include every counted update');
  if (v.totalCount !== null && v.totalCount < v.items.length) error('List exceeds total');
  if (v.status === 'unavailable' && (v.items.length > 0 || v.totalCount !== null)) error('Unavailable search has no result');
  if (new Set(v.items.map(i => `${i.id}:${i.revision}`)).size !== v.items.length) error('Duplicate update identity');
});
export type WindowsUpdates = z.infer<typeof windowsUpdatesSchema>;

/**
 * Windows Update paused in Settings or by policy, per kind (WRMM-310). A paused device isn't offered updates, so its empty
 * cached search doesn't mean up to date. `paused` null: the device couldn't tell. `until`: when the pause ends, null when
 * Windows doesn't say. Optional so deployed older agents still ingest.
 */
const updatePauseState = z.object({ paused: z.boolean().nullable(), until: iso.nullable() }).strict()
  .refine(p => p.paused === true || p.until === null, 'Only a pause has an end');
export const updatePauseSchema = z.object({ quality: updatePauseState, feature: updatePauseState }).strict();

export const factsSchema = z.object({
  hostname: text(255).min(1),
  os: z.object({
    family: z.enum(['windows', 'macos', 'linux']), name: text(120), version: text(40),
    kernelVersion: text(40).optional(), productType: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    nativeArchitecture: z.enum(['x64', 'arm64', 'x86']).optional(),
    build: text(40).optional(), edition: text(80).optional(), architecture: z.enum(['x64', 'arm64', 'x86']).optional(),
  }).strict(),
  hardware: z.object({
    manufacturer: text(120).optional(), model: text(120).optional(), serial: text(80).optional(),
    chassis: z.enum(['laptop', 'desktop', 'server', 'virtual', 'tablet', 'other']),
    cpu: z.object({ model: text(160), cores: z.number().int().min(1).max(1024).nullable(), threads: z.number().int().min(1).max(4096) }).strict(),
    memoryBytes: bytes, tpm: z.object({ present: z.boolean(), version: text(20).optional() }).strict().optional(),
    secureBoot: z.boolean().nullable().optional(),
  }).strict(),
  bootAt: iso.nullable(),
  loggedInUsers: z.array(text(120)).max(16),
  network: z.object({
    domain: text(255).optional(),
    interfaces: z.array(z.object({ name: text(120), mac: text(32).optional(),
      ipv4: z.array(text(15)).max(8), ipv6: z.array(text(45)).max(8) }).strict()).max(16),
  }).strict(),
  security: z.object({
    antivirus: z.array(z.object({ name: text(120), enabled: z.boolean().nullable(), realTimeProtection: z.boolean().nullable(),
      mode: z.enum(['active', 'passive', 'initializing', 'unknown']).optional(),
      signaturesUpdatedAt: iso.nullable() }).strict()).max(5).nullable(),
    firewall: z.object({ enabled: z.boolean().nullable() }).strict(),
    diskEncryption: z.enum(['on', 'off', 'partial', 'unknown']),
    /**
     * What reaches WITTCO Remote from the network (WRMM-267; agents after 0.7.1 with WITTCO Remote): inbound firewall rules
     * letting traffic through to its MeshAgent (Windows) and what MeshAgent listens on beyond loopback (`udp 0.0.0.0:38419`).
     * Sessions only go out through the relay, so both should be empty. Null: the device couldn't check; absent: not reported.
     * `owners` (WRMM-372; Windows, only when something listens): which of its MeshAgent processes holds each socket, by
     * protocol and port, never an address or command line; a diagnostic that leaves the finding as it is.
     */
    remoteInbound: z.object({ rules: z.array(text(200)).max(16), listening: z.array(text(60)).max(32),
      owners: z.array(remoteInboundOwner).max(32).optional() }).strict().nullable().optional(),
  }).strict(),
  updates: z.object({
    pendingReboot: z.boolean().nullable(), pendingRebootSince: iso.nullable(), lastInstalledAt: iso.nullable(),
    pendingCount: z.number().int().min(0).max(10000).nullable(),
    /** Of the waiting updates, how many are already downloaded but not installed (agent 0.5.1 on). */
    readyCount: z.number().int().min(0).max(10000).nullable().optional(),
    windows: windowsUpdatesSchema.optional(),
    pause: updatePauseSchema.optional(),
  }).strict(),
  linux: z.object({
    distribution: text(120), packageManager: z.enum(['apt', 'dnf', 'unknown']),
    desktop: z.enum(['headless', 'x11', 'wayland', 'unknown']), tmux: z.boolean(),
    waylandPortal: z.boolean().optional(),
    desktopObservedAt: iso.optional(),
    failedServices: z.array(text(120)).max(64).nullable(), selinux: text(40).nullable(), updateSource: z.literal('local-cache'),
  }).strict().optional(),
  /** `features`: what the agent can do beyond its version, such as checking PowerShell session approvals (WRMM-82). */
  agent: z.object({ version: text(40), architecture: z.enum(['x64', 'arm64']).optional(), features: z.array(text(40)).max(20).optional() }).strict(),
  /** Who is signed in at the computer and how long since their last input, from the latest metrics (WRMM-159). */
  presence: z.object({ observedAt: iso, sessions: presenceSessions }).strict().optional(),
}).strict();
export type DeviceFacts = z.infer<typeof factsSchema>;
/** Facts as a device reports them at enrollment and in inventory: presence comes only with metrics, never from these. */
export const reportedFactsSchema = factsSchema.omit({ presence: true });

export const driveTypeSchema = z.enum(['fixed', 'removable', 'network', 'unknown']);

const volume = z.object({
  driveType: driveTypeSchema.optional(),
  mount: text(120).min(1), label: text(120).optional(), filesystem: text(40).optional(),
  totalBytes: bytes, freeBytes: bytes, system: z.boolean(), encrypted: z.boolean().nullable(),
}).strict().refine(v => v.freeBytes <= v.totalBytes, 'Free space exceeds capacity');

// Sent as UTF-8 JSON (non-ASCII text not \u-escaped); at the bounds below that stays under the 5 MB inventory body limit.
export const inventorySchema = z.object({
  collectedAt: iso, facts: reportedFactsSchema, volumes: z.array(volume).max(32),
  software: z.array(z.object({ name: text(200).min(1), version: text(80).default(''), publisher: text(200).optional(),
    installedOn: z.iso.date().optional(), scope: z.enum(['machine', 'user']).optional() }).strict()).max(3000),
  /** Complete machine software and adapter snapshots only; never infer changes from a failed read. */
  activitySnapshot: z.object({ softwareComplete: z.boolean(),
    adapters: z.array(z.object({ name: text(120).min(1), up: z.boolean() }).strict()).max(32).nullable(),
  }).strict().optional(),
}).strict();
export type InventoryReport = z.infer<typeof inventorySchema>;

export const metricsSchema = z.object({
  presence: z.object({ at: iso, sessions: presenceSessions }).strict().optional(),
  linuxDesktop: z.object({ desktop: z.enum(['headless', 'x11', 'wayland', 'unknown']),
    waylandPortal: z.boolean(), at: iso }).strict().optional(),
  samples: z.array(z.object({
    at: iso, cpuPct: z.number().min(0).max(100), memoryUsedBytes: bytes, memoryTotalBytes: bytes,
    volumes: z.array(z.object({ mount: text(120).min(1), freeBytes: bytes, totalBytes: bytes }).strict()).max(8).optional(),
  }).strict()).min(1).max(120), // Two hours of per-minute samples; longer offline gaps stay a gap.
}).strict();
export type MetricsReport = z.infer<typeof metricsSchema>;

export const runStatusSchema = z.object({
  status: z.enum(['running', 'succeeded', 'failed', 'canceled', 'unknown_outcome']),
  exitCode: z.number().int().min(-2147483648).max(4294967295).optional(),
  stdout: z.string().max(65536).optional(), stderr: z.string().max(16384).optional(), truncated: z.boolean().optional(),
}).strict();

export const enrollSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/), facts: reportedFactsSchema }).strict();
/** A device replacing its key (WRMM-126): `key` is a compact JWS the new key signed (agent-auth.ts verifyNewKey). */
export const credentialSchema = z.object({ key: z.string().max(2000).regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/) }).strict();

/** What a device learns about itself from GET /agent/v1/context (WRMM-122): names for its tray, and the MSP's
 *  support contact (each field null when unset). No IDs beyond what the device already has. */
export interface DeviceContextView {
  customerName: string; siteName: string; deviceName: string;
  support: { url: string | null; phone: string | null; email: string | null };
}

// Health signals are computed on the server from telemetry, never reported by the device.
export const signalKeys = ['disk.low', 'disk.filling', 'memory.pressure', 'cpu.sustained', 'reboot.pending', 'uptime.long',
  'security.av_off', 'security.av_stale', 'security.firewall_off', 'security.encryption_off', 'security.remote_inbound',
  'os.unsupported', 'os.ending', 'patch.stale', 'patch.paused'] as const;
export type SignalKey = typeof signalKeys[number];
export type Severity = 'critical' | 'warning' | 'info';
export interface DeviceSignal {
  id: string; key: SignalKey; severity: Severity; status: 'open' | 'resolved'; title: string; detail: string;
  evidence: Record<string, unknown>; firstSeenAt: string; lastSeenAt: string; resolvedAt: string | null;
  /** A person's review of an open signal (WRMM-160); `covers` is false once it got worse or names another volume. */
  review?: SignalReview | null;
}
export type SignalDisposition = 'benign' | 'resolved';
export interface SignalReview {
  disposition: SignalDisposition; note: string | null; by: string; byMe: boolean; at: string; severity: Severity; covers: boolean;
}
export const signalReviewSchema = z.object({ disposition: z.enum(['benign', 'resolved']), note: z.string().trim().max(500).default('') }).strict();

export interface Lifecycle {
  status: 'supported' | 'ending' | 'unsupported' | 'unknown'; label: string; endsAt: string | null; note: string | null; source: string | null;
}
export interface VolumeView {
  driveType?: z.infer<typeof driveTypeSchema>;
  mount: string; label: string | null; filesystem: string | null; totalBytes: number; freeBytes: number; system: boolean;
  encrypted: boolean | null; projection: { daysToFull: number | null; bytesPerDay: number | null; samples: number };
  history: Array<{ at: string; freeBytes: number }>;
}
export interface DeviceTelemetry {
  deviceId: string; managed: boolean; agentLastSeenAt: string | null;
  facts: DeviceFacts | null; collectedAt: string | null; lifecycle: Lifecycle | null;
  volumes: VolumeView[];
  metrics: { range: '24h' | '7d'; bucketMinutes: number; points: Array<{ at: string; cpuPct: number | null; memoryPct: number | null }> };
  current: { cpuPct: number | null; memoryPct: number | null; at: string | null };
  /** Open signals that need attention; open ones a person reviewed (still covered); those resolved in the last week. */
  signals: { open: DeviceSignal[]; reviewed: DeviceSignal[]; resolved: DeviceSignal[] };
  softwareCount: number;
}
export interface SoftwareItem { name: string; version: string; publisher: string | null; installedOn: string | null }

// Operations
/** What removing a device did (WRMM-70). The counts are null when the removal was confirmed only after an error. */
export interface DeviceRemoval { hostname: string; jobsCanceled: number | null; runsUnknown: number | null; sessionsEnded: number | null; remoteNodes: string[] }

export type Capability = 'devices.enroll' | 'diagnostics.run' | 'scripts.run' | 'jobs.approve' | 'remote.connect';
export interface ReviewFinding { severity: 'high' | 'medium' | 'info'; title: string; detail: string; line: number | null; excerpt: string | null }
export type RunbookParameter =
  | { name: string; label: string; type: 'integer'; min: number; max: number; default: number }
  | { name: string; label: string; type: 'string'; minLength: number; maxLength: number; pattern?: string; default?: string };
export type RunbookParameters = Record<string, number | string>;
export interface RunbookSummary {
  id: string; version: number; title: string; summary: string; os: Array<'windows' | 'macos' | 'linux'>;
  /** Positive agent protocol/pin-set support; never inferred from a version string. */
  requiredAgentFeatures?: string[];
  effect: 'read' | 'change'; disruption: 'none' | 'brief' | 'restart'; runAs: 'system';
  timeoutSeconds: number; parameters: RunbookParameter[]; addresses: SignalKey[]; verification: string;
  /** What may stop it working, shown wherever it is offered (WRMM-173: Tamper Protection and the Defender repair). */
  caveat?: string;
  script: string; scriptSha256: string;
}
export type JobState = 'awaiting_approval' | 'rejected' | 'queued' | 'delivered' | 'running' | 'succeeded' | 'failed'
  | 'canceled' | 'expired' | 'unknown_outcome';
export interface JobView {
  id: string; deviceId: string; deviceTitle: string | null; organizationId: string; siteId: string | null; organizationName: string | null; siteName: string | null;
  operation: 'runbook' | 'script' | 'agent_update' | 'windows_patch'; runbookId: string | null; runbookVersion: number | null; title: string;
  windowsPatch?: { manifest: import('../operations/windows-patch-plan').PatchManifest | null;
    result: import('../operations/windows-patch-plan').PatchResult | null };
  /** For an agent update: the signed release it installs. Its manifest is the job's `script`. */
  release: { version: string; sha256: string; size: number; floor: string } | null;
  /** Set when the job was requested for several devices at once, which can be approved together. */
  batchId: string | null;
  /** The Script library template version a script job was made from (WRMM-230). The job holds its own copy of the bytes. */
  template: { id: string; version: number; name: string } | null;
  // The exact bytes (up to 64 KiB) come only from GET /v1/jobs/:id; lists send null, with the hash and review.
  installCompatibility?: { requirements: PackageRequirements; result: CompatibilityAssessment | null };
  parameters: Record<string, unknown>; script: string | null; scriptSha256: string; runAs: 'system' | 'user';
  architecture: 'x64' | 'arm64'; shell?: 'powershell' | 'bash' | 'zsh'; timeoutSeconds: number; effect: 'read' | 'change'; reason: string;
  approvalRequired: boolean; actionDigest: string; review: ReviewFinding[];
  /** Null only for an automatic agent update (WRMM-87): queued by the server, approved by the release's own signatures. */
  requestedBy: string | null; requestedByMe: boolean; automatic: boolean; requestedAt: string; expiresAt: string;
  state: JobState; canceledAt: string | null;
  approval: { decision: 'approved' | 'rejected'; decidedBy: string; decidedByMe: boolean; decidedAt: string; method: string;
    userVerified: boolean | null; selfApproved: boolean; note: string | null;
    /** Approved by the requester's PowerShell session (WRMM-82) rather than a key touch of its own. */
    scriptSessionId: string | null;
    /** Approved by an agent window (WRMM-397) its issuing owner opened with one key touch. */
    agentWindowId?: string | null } | null;
  run: { id: string; status: string; deliveredAt: string; startedAt: string | null; finishedAt: string | null;
    exitCode: number | null; stdout: string | null; stderr: string | null; truncated: boolean } | null;
  /** Someone looked at this failed or unknown-outcome job (WRMM-91): it no longer needs attention; its state stays. */
  checked: { by: string; byMe: boolean; at: string; note: string | null } | null;
  /** When its device was removed. A failed or unknown-outcome job not checked by then was closed by the removal (WRMM-226): nothing more can be done there; its state stays. */
  deviceRemovedAt: string | null;
}
export const jobCheckSchema = z.object({ note: z.string().trim().max(500).default('') }).strict();
// Scripts are limited in UTF-8 bytes, the form they are hashed, stored and sent in; the console counts the same way.
export const SCRIPT_MAX_BYTES = 65536;
const utf8 = new TextEncoder();
export const scriptBytes = (text: string) => utf8.encode(text).length;
export const scriptTextSchema = z.string().refine(t => scriptBytes(t) <= SCRIPT_MAX_BYTES);
const parameterValues = z.record(z.string().regex(/^[A-Za-z][A-Za-z0-9]{0,63}$/), z.union([z.number().int().safe(), z.string().max(256).regex(/^[^\p{Cc}\p{Cf}\p{Cs}]*$/u)]));
/** Run a Script library template (WRMM-230): the version the person was shown, with values for its parameters. */
const templateRunSchema = z.object({ id: z.uuid(), version: z.number().int().positive(), parameters: parameterValues.default({}) }).strict();
/** A catalog runbook with values for its parameters, and a script with how to run it: shared by people's and agents' requests. */
export const runbookRunSchema = z.object({ id: z.string().max(80), parameters: parameterValues.default({}) }).strict();
export const scriptRunSchema = z.object({ text: scriptTextSchema.min(1), shell: z.enum(['bash', 'zsh']).optional(), runAs: z.enum(['system', 'user']),
  timeoutSeconds: z.number().int().min(10).max(3600).default(300) }).strict();
export const jobRequestSchema = z.object({
  deviceId: z.uuid(), reason: z.string().trim().min(1).max(500),
  runbook: runbookRunSchema.optional(),
  script: scriptRunSchema.optional(),
  update: z.object({ version: z.string().regex(/^(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})$/) }).strict().optional(),
  template: templateRunSchema.optional(),
}).strict().refine(r => [r.runbook, r.script, r.update, r.template].filter(Boolean).length === 1, 'Choose a runbook, a script, a template or an agent update');
export type JobRequest = z.infer<typeof jobRequestSchema>;
/** The same runbook, script, template or agent update for several devices, each its own job, approvable with one key touch. */
export const MAX_BATCH = 200;
export const jobBatchRequestSchema = z.object({
  deviceIds: z.array(z.uuid()).min(1).max(MAX_BATCH).refine(ids => new Set(ids).size === ids.length, 'Each device once'),
  reason: z.string().trim().min(1).max(500),
  runbook: runbookRunSchema.optional(),
  script: scriptRunSchema.optional(),
  update: z.object({ version: z.string().regex(/^(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})$/) }).strict().optional(),
  template: templateRunSchema.optional(),
}).strict().refine(r => [r.runbook, r.script, r.update, r.template].filter(Boolean).length === 1, 'Choose a runbook, a script, a template or an agent update');
export type JobBatchRequest = z.infer<typeof jobBatchRequestSchema>;
export interface JobBatchView {
  id: string; title: string; requestedByMe: boolean; requestedAt: string; digest: string;
  /** How many jobs the batch holds; `jobs` may be fewer if this person can't see every device. */
  size: number;
  /** The batch's jobs this person can see, newest state first. */
  jobs: JobView[];
  /** Only when the batch was just created: devices that couldn't take the job, and why. */
  skipped?: Array<{ deviceId: string; reason: string }>;
}

/**
 * The Script library (WRMM-230): an MSP's own Windows PowerShell templates. Each save is a new, immutable version with
 * its own bytes and SHA-256; running one makes an ordinary script job with that version's exact bytes, approved per job.
 * String parameters take no pattern: their length and the no-control-characters rule bound them.
 */
export type TemplateParameter =
  | { name: string; label: string; type: 'integer'; min: number; max: number; default: number }
  | { name: string; label: string; type: 'string'; minLength: number; maxLength: number; default?: string };
export interface ScriptTemplateView {
  id: string; name: string; description: string; os: 'windows'; runAs: 'system' | 'user'; timeoutSeconds: number; defaultReason: string;
  version: number; scriptSha256: string; parameters: TemplateParameter[]; review: ReviewFinding[];
  updatedAt: string; updatedByMe: boolean; archivedAt: string | null;
  /** Whether this person may save versions or archive it: an MSP-wide membership that may run scripts. */
  canEdit: boolean;
}
/** One template with its current bytes (or the version asked for) and every version's hash. */
export interface ScriptTemplateDetail extends ScriptTemplateView {
  shown: { version: number; script: string; scriptSha256: string; parameters: TemplateParameter[]; runAs: 'system' | 'user'; timeoutSeconds: number; review: ReviewFinding[] };
  versions: Array<{ version: number; scriptSha256: string; createdAt: string; createdByMe: boolean }>;
}
export interface ScriptLibraryView { templates: ScriptTemplateView[]; canEdit: boolean }
const parameterName = z.string().regex(/^[A-Za-z][A-Za-z0-9]{0,63}$/);
const parameterText = z.string().max(256).regex(/^[^\p{Cc}\p{Cf}\p{Cs}]*$/u);
const parameterLabel = z.string().trim().min(1).max(80).regex(/^[^\p{Cc}\p{Cf}\p{Cs}]*$/u);
export const templateParameterSchema = z.discriminatedUnion('type', [
  z.object({ name: parameterName, label: parameterLabel, type: z.literal('integer'), min: z.number().int().safe(), max: z.number().int().safe(), default: z.number().int().safe() })
    .strict().refine(p => p.min <= p.default && p.default <= p.max, 'The default must be within the range'),
  z.object({ name: parameterName, label: parameterLabel, type: z.literal('string'), minLength: z.number().int().min(0).max(256), maxLength: z.number().int().min(1).max(256),
    default: parameterText.optional() }).strict()
    .refine(p => p.minLength <= p.maxLength && (p.default === undefined || (p.default.length >= p.minLength && p.default.length <= p.maxLength && p.default.trim() === p.default)),
      'The default must fit the length limits'),
]);
export const scriptTemplateSchema = z.object({
  name: z.string().trim().min(1).max(80).regex(/^[^\p{Cc}\p{Cf}\p{Cs}]*$/u), description: z.string().trim().max(1000).default(''),
  runAs: z.enum(['system', 'user']), timeoutSeconds: z.number().int().min(10).max(3600).default(300), defaultReason: z.string().trim().max(500).default(''),
  script: scriptTextSchema.min(1),
  parameters: z.array(templateParameterSchema).max(10).default([])
    .refine(ps => new Set(ps.map(p => p.name.toLowerCase())).size === ps.length, 'Each parameter name once'),
}).strict();
export type ScriptTemplateInput = z.infer<typeof scriptTemplateSchema>;
/** A new version must say which version it replaces, so two people saving at once can't overwrite each other unseen. */
export const scriptTemplateVersionSchema = scriptTemplateSchema.extend({ expectedVersion: z.number().int().positive() }).strict();

/** A customer's automatic agent updates (WRMM-87): on unless an owner paused them. */
export interface AutomaticUpdatesView { organizationId: string; enabled: boolean; canChange: boolean }
export const automaticUpdatesSchema = z.object({ enabled: z.boolean() }).strict();

/** A signed agent release devices can be updated to. */
export interface AgentReleaseView {
  platform: 'windows-x64' | 'windows-arm64' | 'linux-x64' | 'linux-arm64' | 'darwin-arm64'; version: string; sha256: string; size: number; floor: string; publishedAt: string; expiresAt: string;
  /** Windows devices in this workspace, as far as this person can see, whose agent is older than this release. */
  outdatedDeviceIds: string[];
}
export interface EnrollmentGrantView {
  id: string; organizationId: string; organizationName: string | null; siteId: string; siteName: string | null; label: string;
  /** Null: it lasts until revoked (WRMM-89). */
  createdAt: string; expiresAt: string | null; maxUses: number; uses: number; revokedAt: string | null; createdByMe: boolean;
}
/**
 * A new grant: its token is shown once, with the server address the agent's install command needs, and the agent version
 * an installer (file or download link) would carry now, or null when none can be built.
 */
export interface EnrollmentGrantIssued { grant: EnrollmentGrantView; token: string; agentServer: string; installerVersion: string | null; installerVersions?: Partial<Record<'windows-x64' | 'windows-arm64', string | null>> }
export interface AuditEventView {
  id: number; at: string; actorType: 'person' | 'device' | 'system' | 'agent'; actorId: string | null; actorIsMe: boolean;
  action: string; targetType: string | null; targetId: string | null; details: Record<string, unknown>;
}
export type AuditCategory = 'access' | 'devices' | 'jobs' | 'remote';
/** An event in the workspace trail, with the device it concerns when the caller can see that device. */
export interface AuditTrailEvent extends AuditEventView { device: { id: string; hostname: string } | null }
export interface AuditTrailPage { events: AuditTrailEvent[]; nextBefore: number | null }
export interface ApprovalKeyView {
  id: string; label: string; createdAt: string; lastUsedAt: string | null; deviceType: string | null; backedUp: boolean | null;
  /** Whether the browser said the key can be offered at sign-in; null when it didn't say. */
  signsIn: boolean | null;
}

/**
 * A PowerShell session (WRMM-82): one key touch approves the person's scripts on one device until `expiresAt`, 15
 * minutes after it was asked for. `live` while it approves new scripts: approved, not ended, more than a minute left
 * and its key still registered. Shown (and endable) until it runs out, since its scripts approved earlier may start.
 */
export interface ScriptSessionView {
  id: string; deviceId: string; reason: string; requestedAt: string; expiresAt: string; approvedAt: string | null; endedAt: string | null;
  mine: boolean; live: boolean;
}
